import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  askRescan,
  createWalkIn,
  freeRoomsFor,
  freeRoomsOfType,
  FreeRooms,
  giveRoom,
  GuestLink,
  markCheckedIn,
  markCheckedOut,
  markIdChecked,
  receptionBoard,
  ReceptionBoard,
  receptionHotels,
  ReceptionHotel,
  ReceptionStay,
  showGuestLink,
} from '../../../../api/stays-hotel-client';
import { StaysApiError } from '../../../../api/stays-client';
import { useAuth } from '../../../../context/auth-provider';
import { useClock } from '../../../../context/clock-context';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { StateSwitch, Staleable } from '../../../../motion';
import { AppButton } from '../../../button/app-button';
import { StatusPill } from '../../../status-pill/status-pill';
import { ClockNote } from '../../../clock-note/clock-note';
import { FormModal } from '../../../modal/form-modal';
import { LogoMark } from '../../../logo/logo-mark';
import { Stepper } from '../../../stepper/stepper';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { toast } from '../../../../utils/toast';
import {
  ACT_LABEL,
  addDays,
  checkinPill,
  dayLabel,
  guestName,
  ID_WORDS,
  mainAction,
  partyLine,
  ReceptionTab,
  roomLine,
  rowsOf,
  TABS,
  WALK_IN_NIGHTS_MAX,
} from './reception-model';
import {
  Brand,
  Counts,
  Empty,
  Head,
  Inline,
  Inner,
  List,
  Meta,
  Name,
  Page,
  People,
  Person,
  Pills,
  QrBox,
  RoomChoice,
  RoomGrid,
  Row,
  RowActs,
  RowMain,
  Sub,
  TabButton,
  Tabs,
  Title,
  Toolbar,
} from './reception.styled';

const failText = (e: unknown, fallback: string): string => refusalText(e instanceof StaysApiError ? e : null, fallback);

/**
 * RECEPTION — /reception (Independent Stays IS9, R-IS9.3…R-IS9.5).
 *
 * A hotel's front desk without the rest of Lobbero Desk: today's arrivals,
 * the guests in house and today's departures, on the PROPERTY's clock (a
 * staff surface: surfaceOfPath reads /reception as a desk screen). One tap
 * gives a stay its room, checks a guest in or out, marks an ID as checked
 * after looking at the document, or shows the guest's check-in link as a QR
 * code for their own phone. "New walk-in" makes a stay from today and shows
 * its link the same way (IS-Q25: no kiosk on the reception device).
 *
 * After a merge with Desk the desk's own lane does this (R-IS9.7): an
 * organisation with Desk is sent to the main menu.
 */
export const ReceptionPage: React.FC = () => {
  const desk = useDeskInPlan();
  const { permissions } = useAuth();
  const canWrite = can(permissions, PERMISSIONS.STAYS_RECEPTION);
  const [hotels, setHotels] = useState<ReceptionHotel[] | null>(null);
  const [hotelsFailed, setHotelsFailed] = useState(false);
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [board, setBoard] = useState<ReceptionBoard | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<ReceptionTab>('arrivals');
  const [busy, setBusy] = useState<string | null>(null);
  const [giving, setGiving] = useState<ReceptionStay | null>(null);
  const [walkIn, setWalkIn] = useState(false);
  const [link, setLink] = useState<{ name: string; link: GuestLink } | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const clock = useClock({ buildingZone: board ? board.tz : null, response: board, surface: 'desk' });

  const loadHotels = useCallback(() => {
    setHotelsFailed(false);
    receptionHotels()
      .then(r => {
        setHotels(r.items);
        setBuildingId(id => (id && r.items.some(h => h.building_id === id) ? id : r.items[0] ? r.items[0].building_id : null));
      })
      .catch(() => setHotelsFailed(true));
  }, []);
  useEffect(loadHotels, [loadHotels]);

  const load = useCallback(
    (quiet = false) => {
      if (!buildingId) return;
      if (quiet) setRefreshing(true);
      else setFailed(false);
      receptionBoard(buildingId, day || undefined)
        .then(b => {
          setBoard(b);
          setFailed(false);
        })
        .catch(() => {
          if (!quiet) setFailed(true);
        })
        .finally(() => setRefreshing(false));
    },
    [buildingId, day]
  );
  useEffect(() => {
    setBoard(null);
    load();
  }, [load]);

  if (desk) return <Navigate to="/main-menu" replace />;

  const act = async (key: string, fn: () => Promise<unknown>, done: string, fallback: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(done);
      load(true);
    } catch (e) {
      toast.error(failText(e, fallback));
      load(true);
    } finally {
      setBusy(null);
    }
  };

  const openLink = async (s: ReceptionStay) => {
    setBusy(`link:${s.reservation_id}`);
    try {
      const out = await showGuestLink(s.reservation_id);
      setLink({ name: guestName(s), link: out });
    } catch (e) {
      toast.error(failText(e, 'Could not show the check-in link.'));
    } finally {
      setBusy(null);
    }
  };

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length;
    setTab(TABS[next].key);
    const el = tabRefs.current[next];
    if (el) el.focus();
  };

  const hotel = hotels && hotels.find(h => h.building_id === buildingId);
  const today = board ? board.today : hotel ? hotel.today : null;
  const rows = board ? rowsOf(board, tab) : [];
  const hotelsState = hotelsFailed ? 'error' : !hotels ? 'loading' : hotels.length ? 'ready' : 'empty';

  return (
    <Page id="main-content" tabIndex={-1} data-reception="">
      <Inner>
        <Head>
          <Brand>
            <LogoMark size={40} aria-hidden="true" />
            <div>
              <Title>Reception{hotel ? ` · ${hotel.name}` : ''}</Title>
              {board ? (
                <Sub>
                  {board.day === board.today
                    ? `Today, ${dayLabel(board.day)}`
                    : board.day === addDays(board.today, 1)
                      ? `Tomorrow, ${dayLabel(board.day)}`
                      : dayLabel(board.day)}{' '}
                  ·{' '}
                  <ClockNote clock={clock} inline />
                </Sub>
              ) : null}
            </div>
          </Brand>
          {canWrite && board ? (
            <AppButton onClick={() => setWalkIn(true)} data-walk-in-open="">
              New walk-in
            </AppButton>
          ) : null}
        </Head>

        <StateSwitch state={hotelsState} minHeight={240}>
          {hotelsState === 'error' ? (
            <LoadError message="Could not load your hotels." onRetry={loadHotels} />
          ) : hotelsState === 'loading' ? (
            <ListSkeleton rows={4} label="your hotels" />
          ) : hotelsState === 'empty' ? (
            <Empty>There is no hotel for you here yet. When a hotel is added and you are given access to it, its arrivals appear here.</Empty>
          ) : (
            <>
              <Toolbar>
                {hotels && hotels.length > 1 ? (
                  <Field>
                    <Label htmlFor="reception-hotel">Hotel</Label>
                    <Select
                      id="reception-hotel"
                      value={buildingId || ''}
                      onChange={e => {
                        setDay(null);
                        setBuildingId(Number(e.target.value));
                      }}
                    >
                      {hotels.map(h => (
                        <option key={h.building_id} value={h.building_id}>
                          {h.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                {today ? (
                  <Inline>
                    <AppButton
                      variant={!day || day === today ? 'primary' : 'secondary'}
                      size="compact"
                      aria-pressed={!day || day === today}
                      onClick={() => setDay(null)}
                    >
                      Today
                    </AppButton>
                    <AppButton
                      variant={day === addDays(today, 1) ? 'primary' : 'secondary'}
                      size="compact"
                      aria-pressed={day === addDays(today, 1)}
                      onClick={() => setDay(addDays(today, 1))}
                    >
                      Tomorrow
                    </AppButton>
                  </Inline>
                ) : null}
              </Toolbar>

              <StateSwitch state={failed ? 'error' : !board ? 'loading' : 'ready'} minHeight={240}>
                {failed ? (
                  <LoadError message="Could not load reception." onRetry={() => load()} />
                ) : !board ? (
                  <ListSkeleton rows={4} label="today's guests" />
                ) : (
                  <Staleable stale={refreshing}>
                    <Counts aria-label="Rooms tonight">
                      {board.room_types.map(rt => (
                        <StatusPill key={rt.id} tone={rt.free ? 'brand' : 'warning'}>
                          {rt.name}: {rt.free} of {rt.rooms} free
                          {rt.waiting ? ` · ${rt.waiting} waiting for a room` : ''}
                        </StatusPill>
                      ))}
                    </Counts>
                    <Tabs role="tablist" aria-label="Guests">
                      {TABS.map((t, i) => (
                        <TabButton
                          key={t.key}
                          ref={el => {
                            tabRefs.current[i] = el;
                          }}
                          type="button"
                          role="tab"
                          id={`reception-tab-${t.key}`}
                          aria-selected={tab === t.key}
                          aria-controls="reception-panel"
                          tabIndex={tab === t.key ? 0 : -1}
                          $active={tab === t.key}
                          onClick={() => setTab(t.key)}
                          onKeyDown={e => onTabKey(e, i)}
                        >
                          {t.label} ({rowsOf(board, t.key).length})
                        </TabButton>
                      ))}
                    </Tabs>
                    <div role="tabpanel" id="reception-panel" aria-labelledby={`reception-tab-${tab}`}>
                      {!canWrite ? <Meta>You can see reception. Changing it needs the Reception role.</Meta> : null}
                      {rows.length === 0 ? (
                        <Empty>
                          {tab === 'arrivals'
                            ? 'No arrivals on this day.'
                            : tab === 'in_house'
                              ? 'Nobody is checked in right now.'
                              : 'No departures on this day.'}
                        </Empty>
                      ) : (
                        <List aria-label={TABS.find(t => t.key === tab)?.label}>
                          {rows.map(s => (
                            <StayRow
                              key={s.reservation_id}
                              stay={s}
                              tab={tab}
                              canWrite={canWrite}
                              busy={busy}
                              onGive={() => setGiving(s)}
                              onIn={() =>
                                act(
                                  `in:${s.reservation_id}`,
                                  () => markCheckedIn(s.reservation_id, s.version),
                                  `${guestName(s)} is checked in.`,
                                  'Could not check the guest in.'
                                )
                              }
                              onOut={() =>
                                act(
                                  `out:${s.reservation_id}`,
                                  () => markCheckedOut(s.reservation_id, s.version),
                                  `${guestName(s)} is checked out.`,
                                  'Could not check the guest out.'
                                )
                              }
                              onLink={() => openLink(s)}
                              onIdChecked={(guestId, who) =>
                                act(`id:${guestId}`, () => markIdChecked(guestId), `${who}'s ID is marked as checked.`, 'Could not mark the ID as checked.')
                              }
                              onRescan={(guestId, who) =>
                                act(`scan:${guestId}`, () => askRescan(guestId), `${who} is asked to scan their ID again.`, 'Could not ask for a new scan.')
                              }
                            />
                          ))}
                        </List>
                      )}
                    </div>
                  </Staleable>
                )}
              </StateSwitch>
            </>
          )}
        </StateSwitch>
      </Inner>

      <GiveRoomModal
        stay={giving}
        onClose={() => setGiving(null)}
        onSaved={s => {
          setGiving(null);
          toast.success(`${guestName(s)} has room ${s.room ? s.room.label : ''}.`);
          load(true);
        }}
      />
      {board ? (
        <WalkInModal
          open={walkIn}
          board={board}
          onClose={() => setWalkIn(false)}
          onSaved={(name, l) => {
            setWalkIn(false);
            load(true);
            if (l) setLink({ name, link: l });
            else toast.success(`${name}'s stay is added.`);
          }}
        />
      ) : null}
      <LinkModal shown={link} onClose={() => setLink(null)} />
    </Page>
  );
};

/* ── one stay ── */

interface StayRowProps {
  stay: ReceptionStay;
  tab: ReceptionTab;
  canWrite: boolean;
  busy: string | null;
  onGive: () => void;
  onIn: () => void;
  onOut: () => void;
  onLink: () => void;
  onIdChecked: (guestId: string, who: string) => void;
  onRescan: (guestId: string, who: string) => void;
}

const StayRow: React.FC<StayRowProps> = ({ stay: s, tab, canWrite, busy, onGive, onIn, onOut, onLink, onIdChecked, onRescan }) => {
  const main = mainAction(s, tab);
  const pill = checkinPill(s);
  const name = guestName(s);

  return (
    <Row data-stay={s.reservation_id} data-room-assigned={s.room_assigned ? 'yes' : 'no'}>
      <RowMain>
        <Name>{name}</Name>
        <Meta>
          {roomLine(s)} · {partyLine(s)}
          {s.expected_arrival_time && tab === 'arrivals' ? ` · arriving about ${s.expected_arrival_time}` : ''}
        </Meta>
        <Pills>
          <StatusPill tone={pill.tone} dot>
            {pill.label}
          </StatusPill>
          {s.status === 'checked_in' ? (
            <StatusPill tone="success" dot>
              Checked in
            </StatusPill>
          ) : null}
          {s.status === 'checked_out' ? <StatusPill tone="neutral">Checked out</StatusPill> : null}
          {s.self_arrived_at && s.status === 'confirmed' ? <StatusPill tone="brand">Guest says they arrived</StatusPill> : null}
          {s.help_open ? <StatusPill tone="alert">Asked for help</StatusPill> : null}
        </Pills>
        {s.guests.length ? (
          <People aria-label={`IDs of ${name}'s party`}>
            {s.guests.map(g => {
              const who = guestName(g);
              const word = ID_WORDS[g.id_state];

              return (
                <Person key={g.guest_id}>
                  <span>{who}</span>
                  <StatusPill tone={word.tone}>{word.label}</StatusPill>
                  {canWrite && g.can_check ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      loading={busy === `id:${g.guest_id}`}
                      loadingLabel="Saving…"
                      onClick={() => onIdChecked(g.guest_id, who)}
                      aria-label={`Mark ${who}'s ID as checked`}
                    >
                      Mark ID checked
                    </AppButton>
                  ) : null}
                  {canWrite && g.id_state === 'needs_look' ? (
                    <AppButton
                      variant="ghost"
                      size="compact"
                      loading={busy === `scan:${g.guest_id}`}
                      loadingLabel="Asking…"
                      onClick={() => onRescan(g.guest_id, who)}
                      aria-label={`Ask ${who} to scan their ID again`}
                    >
                      Ask for a new scan
                    </AppButton>
                  ) : null}
                </Person>
              );
            })}
          </People>
        ) : null}
      </RowMain>
      {canWrite ? (
        <RowActs>
          {main === 'give_room' ? (
            <AppButton onClick={onGive} aria-label={`Give ${name} a room`}>
              {ACT_LABEL.give_room}
            </AppButton>
          ) : null}
          {main === 'check_in' ? (
            <AppButton
              onClick={onIn}
              loading={busy === `in:${s.reservation_id}`}
              loadingLabel="Checking in…"
              aria-label={`${name} checked in`}
            >
              {ACT_LABEL.check_in}
            </AppButton>
          ) : null}
          {main === 'check_out' ? (
            <AppButton
              onClick={onOut}
              loading={busy === `out:${s.reservation_id}`}
              loadingLabel="Checking out…"
              aria-label={`${name} checked out`}
            >
              {ACT_LABEL.check_out}
            </AppButton>
          ) : null}
          {s.room_assigned && main === 'check_in' ? (
            <AppButton variant="secondary" onClick={onGive} aria-label={`Move ${name} to another room`}>
              Change room
            </AppButton>
          ) : null}
          {s.status === 'confirmed' && !s.checkin.complete && !s.access_revoked ? (
            <AppButton
              variant="secondary"
              onClick={onLink}
              loading={busy === `link:${s.reservation_id}`}
              loadingLabel="Opening…"
              aria-label={`Show ${name}'s check-in link`}
            >
              Show check-in link
            </AppButton>
          ) : null}
        </RowActs>
      ) : null}
    </Row>
  );
};

/* ── give or change a room ── */

const GiveRoomModal: React.FC<{ stay: ReceptionStay | null; onClose: () => void; onSaved: (s: ReceptionStay) => void }> = ({
  stay,
  onClose,
  onSaved,
}) => {
  const [free, setFree] = useState<FreeRooms | null>(null);
  const [failed, setFailed] = useState(false);
  const [pick, setPick] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    if (!stay) return;
    setFailed(false);
    setFree(null);
    freeRoomsFor(stay.reservation_id)
      .then(setFree)
      .catch(() => setFailed(true));
  }, [stay]);
  useEffect(() => {
    setPick('');
    setProblem(null);
    load();
  }, [load]);

  const save = async () => {
    if (!stay || !pick) return;
    setSaving(true);
    setProblem(null);
    try {
      onSaved(await giveRoom(stay.reservation_id, pick, stay.version));
    } catch (e) {
      setProblem(failText(e, 'Could not give the room. Please try again.'));
      load();
    } finally {
      setSaving(false);
    }
  };
  const rooms = free ? free.rooms.filter(r => !stay || !stay.room || r.listing_id !== stay.room.listing_id) : [];

  return (
    <FormModal
      open={!!stay}
      title={stay ? `A room for ${guestName(stay)}` : 'A room'}
      subtitle={stay ? `${stay.room_type_name || 'Room'} · ${dayLabel(stay.check_in)} to ${dayLabel(stay.check_out)}` : undefined}
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton onClick={save} loading={saving} loadingLabel="Saving…" disabled={!pick}>
            Give this room
          </AppButton>
        </Inline>
      }
    >
      {failed ? (
        <LoadError message="Could not load the free rooms." onRetry={load} />
      ) : !free ? (
        <ListSkeleton rows={2} label="the free rooms" />
      ) : rooms.length === 0 ? (
        <Meta>No room of this type is free for all of these nights.</Meta>
      ) : (
        <Field>
          <Label as="span" id="give-room-label">
            Free rooms for these nights
          </Label>
          <RoomGrid role="radiogroup" aria-labelledby="give-room-label">
            {rooms.map(r => (
              <RoomChoice
                key={r.listing_id}
                type="button"
                role="radio"
                aria-checked={pick === r.listing_id}
                $checked={pick === r.listing_id}
                onClick={() => setPick(r.listing_id)}
                data-room={r.label}
              >
                {r.label}
              </RoomChoice>
            ))}
          </RoomGrid>
        </Field>
      )}
      {problem ? <FieldError id="give-room-problem" message={problem} /> : null}
    </FormModal>
  );
};

/* ── a walk-in ── */

const WalkInModal: React.FC<{
  open: boolean;
  board: ReceptionBoard;
  onClose: () => void;
  onSaved: (name: string, link: GuestLink | null) => void;
}> = ({ open, board, onClose, onSaved }) => {
  const types = board.room_types;
  const [typeId, setTypeId] = useState('');
  const [nights, setNights] = useState(1);
  const [party, setParty] = useState(2);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [room, setRoom] = useState('');
  const [free, setFree] = useState<FreeRooms | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState<'qr' | 'email' | null>(null);
  useEffect(() => {
    if (!open) return;
    setTypeId(types.find(t => t.free > 0)?.id || (types[0] ? types[0].id : ''));
    setNights(1);
    setParty(2);
    setFirst('');
    setLast('');
    setEmail('');
    setRoom('');
    setErrors({});
    setProblem(null);
  }, [open]);
  useEffect(() => {
    setFree(null);
    setRoom('');
    if (!open || !typeId) return;
    freeRoomsOfType(typeId, board.today, addDays(board.today, nights))
      .then(setFree)
      .catch(() => setFree(null));
  }, [open, typeId, nights, board.today]);

  const save = async (how: 'qr' | 'email') => {
    const local: Record<string, string> = {};
    if (!typeId) local.room_type_id = 'Choose a room type.';
    if (!first.trim()) local.lead_first_name = 'Type the guest’s first name.';
    if (!last.trim()) local.lead_last_name = 'Type the guest’s last name.';
    if (how === 'email' && !email.trim()) local.lead_email = 'Type the guest’s e-mail to send the link.';
    if (Object.keys(local).length) {
      setErrors(local);

      return;
    }
    setSaving(how);
    setErrors({});
    setProblem(null);
    try {
      const out = await createWalkIn({
        building_id: board.building_id,
        room_type_id: typeId,
        nights,
        party_size: party,
        lead_first_name: first.trim(),
        lead_last_name: last.trim(),
        ...(email.trim() ? { lead_email: email.trim() } : {}),
        ...(room ? { listing_id: room } : {}),
        send_email: how === 'email',
      });
      const name = `${first.trim()} ${last.trim()}`;
      if (how === 'email') {
        toast.success(out.email === 'failed' ? `${name}'s stay is added. The e-mail did not go: show the QR code instead.` : `${name}'s stay is added and the link is on its way.`);
        onSaved(name, out.email === 'failed' ? out.link : null);
      } else onSaved(name, out.link);
    } catch (e) {
      const se = e instanceof StaysApiError ? e : null;
      if (se && se.code === 'INVALID_FIELDS') setErrors(se.fields || {});
      else setProblem(failText(se, 'Could not add the walk-in. Please try again.'));
    } finally {
      setSaving(null);
    }
  };
  const eid = (k: string) => `walk-in-${k}-error`;

  return (
    <FormModal
      open={open}
      title="New walk-in"
      subtitle={`From today, ${dayLabel(board.today)}. The guest checks in on their own phone.`}
      onClose={onClose}
      footer={
        <Inline>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton variant="secondary" onClick={() => save('email')} loading={saving === 'email'} loadingLabel="Sending…" disabled={!!saving}>
            Send the link by e-mail
          </AppButton>
          <AppButton onClick={() => save('qr')} loading={saving === 'qr'} loadingLabel="Adding…" disabled={!!saving}>
            Add and show QR code
          </AppButton>
        </Inline>
      }
    >
      <FieldRow>
        <Field>
          <Label htmlFor="walk-in-type">Room type</Label>
          <Select id="walk-in-type" value={typeId} aria-invalid={!!errors.room_type_id} onChange={e => setTypeId(e.target.value)}>
            {types.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.free} free tonight)
              </option>
            ))}
          </Select>
          <FieldError id={eid('room_type_id')} message={errors.room_type_id} />
        </Field>
        <Field>
          <Label as="span">Nights</Label>
          <Stepper value={nights} min={1} max={WALK_IN_NIGHTS_MAX} label="nights" onChange={setNights} />
          <FieldError id={eid('nights')} message={errors.nights} />
        </Field>
      </FieldRow>
      <FieldRow>
        <Field>
          <Label htmlFor="walk-in-first">First name</Label>
          <Input
            id="walk-in-first"
            value={first}
            maxLength={80}
            autoComplete="off"
            aria-invalid={!!errors.lead_first_name}
            aria-describedby={errors.lead_first_name ? eid('lead_first_name') : undefined}
            onChange={e => setFirst(e.target.value)}
          />
          <FieldError id={eid('lead_first_name')} message={errors.lead_first_name} />
        </Field>
        <Field>
          <Label htmlFor="walk-in-last">Last name</Label>
          <Input
            id="walk-in-last"
            value={last}
            maxLength={80}
            autoComplete="off"
            aria-invalid={!!errors.lead_last_name}
            aria-describedby={errors.lead_last_name ? eid('lead_last_name') : undefined}
            onChange={e => setLast(e.target.value)}
          />
          <FieldError id={eid('lead_last_name')} message={errors.lead_last_name} />
        </Field>
      </FieldRow>
      <FieldRow>
        <Field>
          <Label as="span">Guests</Label>
          <Stepper value={party} min={1} max={30} label="guests" onChange={setParty} />
          <FieldError id={eid('party_size')} message={errors.party_size} />
        </Field>
        <Field>
          <Label htmlFor="walk-in-email">E-mail (to send the link)</Label>
          <Input
            id="walk-in-email"
            type="email"
            value={email}
            maxLength={254}
            autoComplete="off"
            aria-invalid={!!errors.lead_email}
            aria-describedby={errors.lead_email ? eid('lead_email') : undefined}
            onChange={e => setEmail(e.target.value)}
          />
          <FieldError id={eid('lead_email')} message={errors.lead_email} />
        </Field>
      </FieldRow>
      <Field>
        <Label htmlFor="walk-in-room">Room</Label>
        <Select id="walk-in-room" value={room} onChange={e => setRoom(e.target.value)}>
          <option value="">Give a room later</option>
          {(free ? free.rooms : []).map(r => (
            <option key={r.listing_id} value={r.listing_id}>
              Room {r.label}
            </option>
          ))}
        </Select>
        <FieldHint>Door details reach the guest once the stay has a room and their check-in is done.</FieldHint>
        <FieldError id={eid('listing_id')} message={errors.listing_id} />
      </Field>
      {problem ? <FieldError id="walk-in-problem" message={problem} /> : null}
    </FormModal>
  );
};

/* ── the guest's link on their own phone ── */

const LinkModal: React.FC<{ shown: { name: string; link: GuestLink } | null; onClose: () => void }> = ({ shown, onClose }) => (
  <FormModal
    open={!!shown}
    title={shown ? `Check-in for ${shown.name}` : 'Check-in link'}
    subtitle="Ask the guest to scan this with their phone's camera. They check in, scan their ID and sign the house rules there."
    onClose={onClose}
    footer={
      <Inline>
        <AppButton onClick={onClose} data-autofocus="">
          Done
        </AppButton>
      </Inline>
    }
  >
    {shown ? (
      <QrBox data-guest-qr="">
        {shown.link.qr_svg ? (
          <img
            src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(shown.link.qr_svg)}`}
            alt={`QR code of ${shown.name}'s check-in link`}
          />
        ) : (
          <Meta>The QR code could not be drawn. Send the link by e-mail from the stay instead.</Meta>
        )}
      </QrBox>
    ) : null}
  </FormModal>
);
